<script setup>
    import { date_options, date_options_2026, getPartnerDay, skill_level_options, seating_options, availability_options, MY_EVENT_TYPE } from '~~/shared/utilities/schedule';

    const { $bus, $clientPosthog } = useNuxtApp();

    const props = defineProps({
        objects: Number,
        teal: {
            type: Boolean,
            required: false,
            default: false,
        },
        advanced: {
            type: Boolean,
            required: false,
            default: false,
        },
        hideAdvanced: {
            type: Boolean,
            required: false,
            default: false,
        },
        startTime: {
            type: String,
            required: false,
            default: '',
        },
        endTime: {
            type: String,
            required: false,
            default: '',
        },
        year: {
            type: Number,
            required: false,
            default: null,
        },
        // Day options from the agenda block — already view-scoped and already
        // filtered for Partner Day access. See `dates` below.
        dayOptions: {
            type: Array,
            required: false,
            default: null,
        },
        roleOptions: {
            type: Array,
            required: false,
            default: () => [{ label: 'All Roles', value: 'all' }],
        },
        // Mirrors the agenda block's `randomize` prop so the Day select
        // can include the otherwise-hidden Partner Day in preview mode.
        randomize: {
            type: Boolean,
            required: false,
            default: false,
        },
        // Whether to render the Day select, Time-range slider, and Roles
        // select inside the drawer body. Parent decides via useMediaQuery
        // (mobile + tablet — desktop shows these inline).
        showAdvancedFilters: {
            type: Boolean,
            required: false,
            default: false,
        },
        // The three session toggles — seats I need to save, sessions with
        // available seats, on demand. On by default, since the agenda wants them
        // whenever seat saving is open.
        //
        // Speakers turns them off (Nick, Asana 1217708959827844): that page
        // filters SPEAKERS, and a speaker can hold several sessions, so none of
        // the three has a meaning there — and they did nothing when toggled.
        //
        // A prop of its own rather than reusing showAdvancedFilters, which the
        // agenda sets from isMobile: gating on that would have taken the toggles
        // off the DESKTOP agenda, where they work and are wanted.
        showSessionToggles: {
            type: Boolean,
            required: false,
            default: true,
        },
        // Header label shown when advanced filters aren't displayed.
        // Agenda keeps "More Filters" (it has a separate inline filter
        // bar); speakers overrides to "Filters" since this is the only
        // filter UI there.
        basicLabel: {
            type: String,
            required: false,
            default: 'More Filters',
        },
        // Adds the "My Event" option to Session Type. My Schedule only — on All
        // Sessions the viewer's own events aren't in the feed at all, so the
        // option would filter to nothing (UNB-582). Parent owns the condition
        // since it's the one that knows which tab is showing.
        showMyEventType: {
            type: Boolean,
            required: false,
            default: false,
        },
    });


    // Prefer the list the agenda block hands down: it is already scoped to the
    // current view AND has Partner Day removed for viewers whose registration
    // isn't approved (UNB-600).
    //
    // Building our own list here is what caused UNB: on mobile the day selector
    // lives in this drawer, so an unapproved viewer was offered Tuesday, picked
    // it, and the agenda's guard immediately bounced them back to Wednesday — a
    // day that was never selectable, presented as though it were. The comment
    // this replaces ("Partner Day shown to all users for the June launch")
    // predates that gate.
    //
    // The local fallback stays for any caller that doesn't pass a list.
    const dates = computed(() => {
        if (props.dayOptions?.length) return props.dayOptions;
        return props.year === 2026 ? date_options_2026 : date_options;
    });
    const role = defineModel('role', { default: 'all' });
    const skill_levels = ref(skill_level_options);
    const all_seating_options = ref(seating_options);

    const open = defineModel('open');
    // Special controls for calendar view
    const time_range = defineModel('time_range');
    const seating = defineModel('seating', 'all');
    const skill_level = defineModel('skill_level', 'all');
    const day = defineModel('day', '2025-09-03');
    const availability = defineModel('availability', { default: 'all' });

    // Theme is stored in a dedicated `themes` model (was `tags`) so the
    // backend can filter against the session_theme property directly.
    const themes = defineModel('themes', { default: () => [] });

    // Gates the seat/availability toggles below: they only mean anything once
    // seat reservations are open. Same source the seat buttons use, so the
    // filters and the buttons appear together rather than drifting apart.
    const { showSeatReservations } = useScheduleControls();

    // Drawer-owned filter toggles (2026 design)
    const seatsNeeded    = defineModel('seatsNeeded',    { type: Boolean, default: false });
    const availableSeats = defineModel('availableSeats', { type: Boolean, default: false });
    const onDemand       = defineModel('onDemand',       { type: Boolean, default: false });

    // ─── Two-group checkbox accordions (Session Type + Theme) ────────────────
    const sessionTypeTags = computed(() => {
        if (!tagData.value) return [];
        const tags = tagData.value['Session Type'] || tagData.value['Format'] || [];
        // My Event rides in as a normal-looking tag and sorts by name with the
        // rest, which lands it between "Meetup" and "Product Spotlight Demo" —
        // the position the comp shows (Figma 3624-47169).
        const withMyEvent = props.showMyEventType ? [...tags, MY_EVENT_TYPE] : [...tags];
        return withMyEvent.sort((a, b) => a.name.localeCompare(b.name));
    });
    const themeTags = computed(() => {
        const tags = tagData.value?.['Theme'] || [];
        return [...tags].sort((a, b) => a.name.localeCompare(b.name));
    });

    // Session Type and Theme are stored in separate URL params so the backend
    // can filter each against its own session_* property (session_type for
    // Session Type; session_theme for Theme) instead of OR'ing across every
    // session_* property as the catch-all filter_tags filter does.
    const sessionTypes = defineModel('sessionTypes', { default: () => [] });

    const asArray = (val) => Array.isArray(val)
        ? val
        : (val ? String(val).split(',').filter(Boolean) : []);

    const themesArr       = computed(() => asArray(themes.value));
    const sessionTypesArr = computed(() => asArray(sessionTypes.value));

    const isThemeChecked       = (id) => themesArr.value.includes(id);
    const isSessionTypeChecked = (id) => sessionTypesArr.value.includes(id);

    const toggleTheme = (id) => {
        const next = [...themesArr.value];
        const i = next.indexOf(id);
        if (i >= 0) next.splice(i, 1);
        else next.push(id);
        themes.value = next;
    };
    const toggleSessionType = (id) => {
        const next = [...sessionTypesArr.value];
        const i = next.indexOf(id);
        if (i >= 0) next.splice(i, 1);
        else next.push(id);
        sessionTypes.value = next;
    };
    const clearThemes       = () => { themes.value = []; };
    const clearSessionTypes = () => { sessionTypes.value = []; };

    const sessionTypeCount = computed(() => sessionTypeTags.value.filter(t => isSessionTypeChecked(t.id)).length);
    const themeCount       = computed(() => themeTags.value.filter(t => isThemeChecked(t.id)).length);

    // Both sections can be open simultaneously; none open by default.
    const openSections = ref(new Set());
    const isSectionOpen = (key) => openSections.value.has(key);
    const toggleSection = (key) => {
        const s = new Set(openSections.value);
        if (s.has(key)) s.delete(key); else s.add(key);
        openSections.value = s;
    };

    // Fetch tag data to look up names from IDs
    const { data: tagData } = await useAsyncData(`all-tags-${props.year || 'default'}`, () => $fetch('/api/v1/session-tags', { params: props.year ? { year: props.year } : {} }));

    const getTagNames = (tagIds) => {
        if (!tagData.value || !tagIds?.length) return [];
        const names = [];
        for (const [groupName, groupTags] of Object.entries(tagData.value)) {
            for (const tag of groupTags) {
                if (tagIds.includes(tag.id)) {
                    names.push(tag.name);
                }
            }
        }
        return names;
    };

    const applyTags = () => {
        const all = [...themesArr.value, ...sessionTypesArr.value];
        $clientPosthog?.capture('agenda_filter_applied', {
            selected_tags: getTagNames(all),
            selected_tags_count: all.length,
            results_count: props.objects,
            is_advanced_mode: props.advanced,
            day: props.advanced ? day.value : null,
            skill_level: props.advanced ? skill_level.value : null,
            seating: props.advanced ? seating.value : null,
        });
        open.value = false;
    };
    const clearTags = () => {
        themes.value = [];
        sessionTypes.value = [];
    };
    const overlayClass = props.teal ? 'filter-overlay-teal' : 'filter-overlay';

    const drawerStyles = {
        overlay: 'bg-[var(--purple)]/80 backdrop-blur-xs',
        content: 'bg-(--pink) ring-0',
    };

    watch(open, (val) => {
        if (val) $bus.emit('drawer-open');
        else $bus.emit('drawer-close');
        const hs = document.getElementById('hubspot-messages-iframe-container');
        if (hs) hs.style.display = val ? 'none' : '';
    });
</script>

<template>
    <UDrawer
        v-model:open="open"
        id="agenda-filters-drawer"
        direction="right"
        :dismissable="true"
        title="Filter"
        :handle="false"
        :handleOnly="true"
        :ui="drawerStyles"
        class="agenda-filters-drawer font-hubspot-sans">
        <template #header>
            <div class="advanced-controls" v-if="advanced">
                <div class="header">
                    <h3>Session Filters</h3>
                    <UButton color="primary" variant="outline" icon="i-lucide-x" class="rounded-full!" @click="open = false" />
                </div>
                <USelect v-model="availability" :items="availability_options" class="w-full select font-hubspot-serif" variant="outline" :ui="{ content: 'z-[2147483648] ring-0 font-hubspot-serif text-white', item: 'font-hubspot-serif text-white py-3' }" />
                <USelect v-if="advanced && !hideAdvanced" v-model="day" :items="dates" class="w-full select" variant="outline" :ui="{ content: 'z-[2147483648]' }" />
                <USelect v-if="advanced && !hideAdvanced" v-model="skill_level" :items="skill_levels" class="w-full select" variant="outline" :ui="{ content: 'z-[2147483648]' }" />
                <USelect v-if="advanced && !hideAdvanced" v-model="seating" :items="all_seating_options" class="w-full select" variant="outline" :ui="{ content: 'z-[2147483648]' }" />
                <div class="time-range" v-if="advanced && !hideAdvanced">
                    <USlider v-model="time_range" :min-steps-between-thumbs="3" :step="1" class="w-full thumbs" variant="md" :ui="{ track: 'bg-(--dark-pink)', thumb: 'time-slider-thumb' }" />
                    <div class="ranges">
                        <span class="label" v-html="startTime"></span>
                        <span class="label" v-html="endTime"></span>
                    </div>
                </div>
            </div>
            <div class="header">
                <div class="headline">
                    <h3>
                        <span class="font-hubspot-serif">{{ showAdvancedFilters ? 'Session Filters' : basicLabel }}</span>
                        <span class="count" v-if="themesArr.length + sessionTypesArr.length">{{ themesArr.length + sessionTypesArr.length }}</span>
                    </h3>
                </div>
                <button
                    type="button"
                    class="closebutton size-[37px] rounded-full border border-(--magenta) text-(--magenta) bg-transparent inline-flex items-center justify-center cursor-pointer transition-colors hover:bg-(--magenta) hover:text-white"
                    aria-label="Close filters"
                    @click="open = false">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                        <path d="M1 1 L13 13 M13 1 L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                    </svg>
                </button>
            </div>
        </template>
        <template #body>
            <div class="inner">

                <!-- Mobile + tablet controls: Day, Time range, Roles.
                     Desktop shows these inline above the schedule. -->
                <div v-if="showAdvancedFilters" class="mobile-controls">
                    <USelect
                        v-model="day"
                        :items="dates"
                        class="w-full select"
                        variant="outline"
                        :ui="{ content: 'z-[2147483648]' }" />
                    <div class="time-range">
                        <USlider
                            v-model="time_range"
                            :min-steps-between-thumbs="3"
                            :step="1"
                            variant="md"
                            class="w-full thumbs"
                            :ui="{ track: 'bg-(--dark-pink)', thumb: 'bg-(--pink) ring-(--magenta) time-slider-thumb' }" />
                        <div class="ranges">
                            <span class="label" v-html="startTime"></span>
                            <span class="label" v-html="endTime"></span>
                        </div>
                    </div>
                    <USelect
                        v-model="role"
                        :items="roleOptions"
                        class="w-full select"
                        variant="outline"
                        :ui="{ content: 'z-[2147483648]' }" />
                </div>

                <!-- Filter Option toggles. These were built and wired end to end —
                     `seats_needed` travels as a URL param, reaches the sessions
                     endpoint, and has its own filter chip — but the block was held
                     behind `v-if="false"` until seat saving launched, then gated
                     on the real signal: seat reservations being open, which is
                     the only time "seats I need to save" means anything.

                     ON DEMAND IS NOT A SEAT FILTER, and used to inherit that gate
                     purely by sharing this div. With seat saving closed — the
                     state once an event is over — it disappeared, exactly when an
                     archive audience wants it most. The two SEAT toggles carry
                     the gate individually now.

                     Gated per-label rather than by splitting this into two
                     blocks: .filter-toggles draws a border-top and owns its
                     padding, so a second container would add a second rule and
                     double the spacing. One container keeps On Demand sitting
                     directly above the Session Type and Theme accordions in both
                     states. -->
                <div v-if="showSessionToggles" class="filter-toggles">
                    <label v-if="showSeatReservations" class="filter-toggle">
                        <span class="label">Only Show Seats I Need to Save</span>
                        <input
                            type="checkbox"
                            role="switch"
                            class="sr-only toggle-input"
                            v-model="seatsNeeded" />
                        <span class="toggle" :class="{ on: seatsNeeded }" aria-hidden="true">
                            <span class="thumb"></span>
                        </span>
                    </label>
                    <label v-if="showSeatReservations" class="filter-toggle">
                        <span class="label">Only Show Sessions With Available Seats</span>
                        <input
                            type="checkbox"
                            role="switch"
                            class="sr-only toggle-input"
                            v-model="availableSeats" />
                        <span class="toggle" :class="{ on: availableSeats }" aria-hidden="true">
                            <span class="thumb"></span>
                        </span>
                    </label>
                    <label class="filter-toggle">
                        <span class="label">Only Show On Demand Sessions</span>
                        <input
                            type="checkbox"
                            role="switch"
                            class="sr-only toggle-input"
                            v-model="onDemand" />
                        <span class="toggle" :class="{ on: onDemand }" aria-hidden="true">
                            <span class="thumb"></span>
                        </span>
                    </label>
                </div>

                <!-- Session Type + Theme accordions -->
                <div class="agenda-filter-accordions">

                    <!-- Session Type -->
                    <div class="filter-accordion session-type">
                        <div class="filter-accordion-head">
                            <div class="title-group">
                                <h4 class="title cursor-pointer" @click="toggleSection('session-type')">Session Type</h4>
                                <span v-if="sessionTypeCount > 0" class="count-badge">{{ sessionTypeCount }}</span>
                                <button
                                    v-if="sessionTypeCount > 0"
                                    type="button"
                                    class="clear-link"
                                    @click="clearSessionTypes">Clear</button>
                            </div>
                            <button
                                type="button"
                                class="accordion-control"
                                :aria-expanded="isSectionOpen('session-type')"
                                aria-controls="agenda-filter-session-type-body"
                                @click="toggleSection('session-type')">
                                <svg v-if="isSectionOpen('session-type')" width="10" height="2" viewBox="0 0 10 2" fill="none" aria-hidden="true">
                                    <path d="M0 1 H10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                                </svg>
                                <svg v-else width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                                    <path d="M5 0 V10 M0 5 H10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                                </svg>
                                <span class="sr-only">{{ isSectionOpen('session-type') ? 'Collapse' : 'Expand' }} Session Type</span>
                            </button>
                        </div>
                        <Transition name="accordion">
                        <div
                            v-if="isSectionOpen('session-type')"
                            id="agenda-filter-session-type-body"
                            class="filter-accordion-body">
                            <label
                                v-for="tag in sessionTypeTags"
                                :key="tag.id"
                                class="checkbox-row">
                                <input
                                    type="checkbox"
                                    class="sr-only checkbox-input"
                                    :checked="isSessionTypeChecked(tag.id)"
                                    @change="toggleSessionType(tag.id)" />
                                <span class="checkbox-box" :class="{ checked: isSessionTypeChecked(tag.id) }" aria-hidden="true">
                                    <svg v-if="isSessionTypeChecked(tag.id)" width="8" height="8" viewBox="0 0 8 8" fill="none">
                                        <path d="M1 4 L3.2 6 L7 1.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                    </svg>
                                </span>
                                <span class="checkbox-label">{{ tag.name }}</span>
                            </label>
                        </div>
                        </Transition>
                    </div>

                    <!-- Theme -->
                    <div class="filter-accordion theme">
                        <div class="filter-accordion-head">
                            <div class="title-group">
                                <h4 class="title cursor-pointer" @click="toggleSection('theme')">Theme</h4>
                                <span v-if="themeCount > 0" class="count-badge">{{ themeCount }}</span>
                                <button
                                    v-if="themeCount > 0"
                                    type="button"
                                    class="clear-link"
                                    @click="clearThemes">Clear</button>
                            </div>
                            <button
                                type="button"
                                class="accordion-control"
                                :aria-expanded="isSectionOpen('theme')"
                                aria-controls="agenda-filter-theme-body"
                                @click="toggleSection('theme')">
                                <svg v-if="isSectionOpen('theme')" width="10" height="2" viewBox="0 0 10 2" fill="none" aria-hidden="true">
                                    <path d="M0 1 H10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                                </svg>
                                <svg v-else width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                                    <path d="M5 0 V10 M0 5 H10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                                </svg>
                                <span class="sr-only">{{ isSectionOpen('theme') ? 'Collapse' : 'Expand' }} Theme</span>
                            </button>
                        </div>
                        <Transition name="accordion">
                        <div
                            v-if="isSectionOpen('theme')"
                            id="agenda-filter-theme-body"
                            class="filter-accordion-body">
                            <label
                                v-for="tag in themeTags"
                                :key="tag.id"
                                class="checkbox-row">
                                <input
                                    type="checkbox"
                                    class="sr-only checkbox-input"
                                    :checked="isThemeChecked(tag.id)"
                                    @change="toggleTheme(tag.id)" />
                                <span class="checkbox-box" :class="{ checked: isThemeChecked(tag.id) }" aria-hidden="true">
                                    <svg v-if="isThemeChecked(tag.id)" width="8" height="8" viewBox="0 0 8 8" fill="none">
                                        <path d="M1 4 L3.2 6 L7 1.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                    </svg>
                                </span>
                                <span class="checkbox-label">{{ tag.name }}</span>
                            </label>
                        </div>
                        </Transition>
                    </div>
                </div>
            </div>
        </template>
        <template #footer>
            <div class="drawer-footer">
                <div class="buttons">
                    <button
                        tabindex="0"
                        class="cta flex items-center rounded-[50px] py-2.5 px-5 max-w-fit font-hubspot-serif text-[calc(17/var(--base-fs)*1rem)] leading-[1.4] tracking-[0.34px] transition-all duration-250 whitespace-nowrap bg-white text-(--magenta) hover:bg-(--magenta) hover:text-white"
                        @click="applyTags"
                        :aria-label="`Close this panel and see ${objects} results`">
                        See Results&nbsp;<span v-if="objects != null">({{ objects }})</span>
                    </button>
                    <button
                        tabindex="0"
                        class="bg-transparent text-[#7D4566] decoration-[#7D4566] decoration-dotted underline underline-offset-3 decoration-1 p-3.75 font-hubspot-serif text-[calc(17/var(--base-fs)*1rem)] leading-[1.4] tracking-[0.34px] transition-all duration-250 hover:no-underline hover:text-(--magenta)"
                        @click="clearTags"
                        aria-label="Unselect all selected tags">
                        Clear All
                    </button>
                </div>
            </div>
        </template>
    </UDrawer>
</template>

<style>
    .agenda-filters-drawer .advanced-controls {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        margin-bottom: 2.3125rem;
    }

    .agenda-filters-drawer .advanced-controls .header {
        margin-bottom: 0.875rem;
    }

    .agenda-filters-drawer .advanced-controls .time-range {
        display: flex;
        flex-direction: column;
        align-items: center;
        border-radius: var(--radius);
        border: 1px solid var(--magenta);
        font-size: 0.875rem;
        font-weight: 300;
        line-height: 1.5625rem;
        letter-spacing: 0.01875rem;
        gap: 1rem;
        padding: 1.5rem 1.125rem 0.65rem 1.125rem;
    }

    .agenda-filters-drawer .advanced-controls .time-range .ranges {
        display: flex;
        justify-content: space-between;
        width: 100%;
    }

    .agenda-filters-drawer .advanced-controls .time-range span.label {
        width: 3.5rem;
        text-align: center;
    }

    .agenda-filters-drawer .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .agenda-filters-drawer .header p {
        color: var(--magenta);
        font-size: 0.875rem;
        font-style: normal;
        font-weight: 300;
        line-height: 1.25rem;
        letter-spacing: 0.0175rem;
    }

    .agenda-filters-drawer .header h3 {
        font-size: calc(30 / var(--base-fs) * 1rem);
        font-weight: 400;
        line-height: 2.25rem;
        letter-spacing: 0;
        color: var(--magenta);
        font-family: var(--font-hubspot-serif);
        display: flex;
        align-items: center;
        margin-bottom: 0.04375rem;
    }

    /* Scoped to the count badge — the title-text spans should inherit the h3
       color/size, so we narrow this rule to `.count` instead of all `span`s. */
    .agenda-filters-drawer .header h3 .count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border-radius: 999px;
        background: var(--magenta);
        color: #fff;
        font-family: var(--font-hubspot-serif);
        font-size: 14px;
        line-height: 1;
        letter-spacing: 0.42px;
        margin-left: 0.5rem;
        flex-shrink: 0;
    }

    /* close button hover handled by UButton color="primary" variant="outline" */

    /* 2026 accordion dividers — override the shared filter component's default */
    .agenda-filters-drawer .tag-group {
        border-bottom-color: #dc95be;
    }

    /* ── Mobile/tablet controls block ─────────────────────────────────────── */
    .agenda-filters-drawer .mobile-controls {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        margin-bottom: 1.5rem;
    }

    .agenda-filters-drawer .mobile-controls .time-range {
        display: flex;
        flex-direction: column;
        align-items: center;
        border-radius: var(--radius);
        border: 1px solid var(--magenta);
        font-size: 0.875rem;
        font-weight: 300;
        line-height: 1.5625rem;
        letter-spacing: 0.01875rem;
        gap: 1rem;
        padding: 1.5rem 1.125rem 0.65rem 1.125rem;
    }

    .agenda-filters-drawer .mobile-controls .time-range .ranges {
        display: flex;
        justify-content: space-between;
        width: 100%;
    }

    .agenda-filters-drawer .mobile-controls .time-range span.label {
        width: 3.5rem;
        text-align: center;
    }

    /* ── Filter Option toggles (2026 More Filters drawer) ───────────────── */
    .agenda-filters-drawer .filter-toggles {
        display: flex;
        flex-direction: column;
        gap: 20px;
        border-top: 1px solid #dc95be;
        padding-top: 24px;
        margin-bottom: 20px;
    }

    .agenda-filters-drawer .filter-toggle {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-bottom: 20px;
        border-bottom: 1px solid #dc95be;
        cursor: pointer;
    }

    /* Keyboard focus indicator — the visible toggle ring tracks the hidden checkbox. */
    .agenda-filters-drawer .filter-toggle .toggle-input:focus-visible + .toggle {
        outline: 2px solid var(--magenta);
        outline-offset: 2px;
    }

    .agenda-filters-drawer .filter-toggle .label {
        font-family: var(--font-hubspot-serif);
        font-size: 20px;
        line-height: 1.3;
        color: var(--magenta);
        width: 210px;
        margin: 0;
    }

    .agenda-filters-drawer .filter-toggle .toggle {
        position: relative;
        width: 52px;
        height: 26px;
        border-radius: 999px;
        background: #dc95be;
        border: none;
        padding: 0;
        cursor: pointer;
        transition: background-color 150ms ease;
        flex-shrink: 0;
    }

    .agenda-filters-drawer .filter-toggle .toggle.on {
        background: var(--magenta);
    }

    .agenda-filters-drawer .filter-toggle .toggle .thumb {
        position: absolute;
        top: 3px;
        left: 3px;
        width: 20px;
        height: 20px;
        border-radius: 999px;
        background: #fff;
        transition: left 150ms ease;
    }

    .agenda-filters-drawer .filter-toggle .toggle.on .thumb {
        left: 29px;
    }

    /* ── Session Type + Theme accordions ─────────────────────────────────── */
    .agenda-filters-drawer .agenda-filter-accordions {
        display: flex;
        flex-direction: column;
        gap: 20px;
    }

    .agenda-filters-drawer .filter-accordion {
        display: flex;
        flex-direction: column;
        gap: 14px;
        padding-bottom: 20px;
    }

    .agenda-filters-drawer .filter-accordion.session-type {
        border-bottom: 1px solid #dc95be;
    }

    .agenda-filters-drawer .filter-accordion-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
    }

    .agenda-filters-drawer .filter-accordion .title-group {
        display: flex;
        gap: 20px;
        align-items: center;
    }

    .agenda-filters-drawer .filter-accordion .title-group > h4 {
        display: inline-flex;
        align-items: center;
        gap: 10px;
        font-family: var(--font-hubspot-serif);
        font-size: 20px;
        line-height: 1.3;
        color: var(--magenta);
        margin: 0;
        white-space: nowrap;
        /* Clickable title — dotted underline reveals on hover (matches the
           session-card title/description hover treatment). */
        text-decoration: underline dotted transparent;
        text-underline-offset: 4px;
        transition: text-decoration-color 150ms ease;
    }

    @media (hover: hover) {
        .agenda-filters-drawer .filter-accordion .title-group > h4:hover {
            text-decoration-color: #7d4566;
        }
    }

    .agenda-filters-drawer .filter-accordion .count-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 21px;
        height: 21px;
        border-radius: 999px;
        background: var(--magenta);
        color: #fff;
        font-family: var(--font-hubspot-serif);
        font-size: 11px;
        line-height: 1;
        letter-spacing: 0.33px;
    }

    .agenda-filters-drawer .filter-accordion .clear-link {
        font-family: var(--font-hubspot-serif);
        font-size: 14px;
        line-height: 1.5;
        letter-spacing: 0.28px;
        color: #7d4566;
        text-decoration: underline dotted;
        background: transparent;
        border: none;
        padding: 0;
        cursor: pointer;
        white-space: nowrap;
    }

    .agenda-filters-drawer .filter-accordion .clear-link:hover {
        text-decoration: none;
        color: var(--magenta);
    }

    .agenda-filters-drawer .filter-accordion .accordion-control {
        width: 30px;
        height: 30px;
        border-radius: 999px;
        border: 1px solid var(--magenta);
        color: var(--magenta);
        background: transparent;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: background-color 150ms ease, color 150ms ease;
        flex-shrink: 0;
    }

    .agenda-filters-drawer .filter-accordion .accordion-control:hover {
        background: var(--magenta);
        color: #fff;
    }

    .agenda-filters-drawer .filter-accordion-body {
        display: flex;
        flex-direction: column;
        gap: 9px;
        width: 100%;
    }

    .agenda-filters-drawer .checkbox-row {
        display: flex;
        gap: 12px;
        align-items: center;
        cursor: pointer;
    }

    .agenda-filters-drawer .checkbox-row:hover .checkbox-label {
        text-decoration: underline dotted;
        text-decoration-color: var(--dark-pink);
    }

    .agenda-filters-drawer .checkbox-box {
        width: 12px;
        height: 12px;
        border: 1px solid #7d4566;
        background: transparent;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .agenda-filters-drawer .checkbox-box.checked {
        background: var(--magenta);
        border-color: var(--magenta);
    }

    .agenda-filters-drawer .checkbox-row .checkbox-input:focus-visible + .checkbox-box {
        outline: 2px solid var(--magenta);
        outline-offset: 2px;
    }

    .agenda-filters-drawer .checkbox-label {
        font-family: var(--font-hubspot-sans);
        font-weight: 300;
        font-size: 14px;
        line-height: 1.5;
        letter-spacing: 0.28px;
        color: var(--magenta);
    }

    /* 2026 divider above the footer actions row — overrides the
       border-gray-light that UDrawer puts on its #footer slot wrapper.
       Sticky so See Results + Clear All stay visible when the list is long. */
    .agenda-filters-drawer div:has(> .drawer-footer) {
        border-top-color: #dc95be;
        position: sticky;
        bottom: 0;
        background: var(--pink);
        z-index: 1;
    }

    /* Pin the drawer to a fixed width on tablet/desktop so refetches that
       briefly hide the "See Results (N)" count can't reflow the panel. */
    .agenda-filters-drawer {
        width: 440px;
        max-width: 440px;
    }

    /* Sub-430 screens — drop the UDrawer 40px slot padding to 24px so the
       footer "Clear All" link can sit on one line, and let the drawer span
       the full viewport width. */
    @media (max-width: 429px) {
        .agenda-filters-drawer div:has(> .drawer-footer),
        .agenda-filters-drawer div:has(> .inner),
        .agenda-filters-drawer div:has(> .header) {
            padding: 1.5rem;
        }
        .agenda-filters-drawer {
            width: 100vw;
            max-width: 100vw;
        }
    }

    .agenda-filters-drawer .drawer-footer .buttons {
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-wrap: nowrap;
        gap: 1.625rem;
        width: 100%;
    }

    @media (min-width: 1024px) {
        .agenda-filters-drawer .drawer-footer .buttons {
            gap: 1.625rem;
        }
    }

    /* Accordion expand/collapse animation */
    .accordion-enter-active { animation: accordion-expand 250ms ease-out; overflow: hidden; }
    .accordion-leave-active { animation: accordion-expand 200ms ease-in reverse; overflow: hidden; }
    @keyframes accordion-expand {
        from { max-height: 0; opacity: 0; }
        to   { max-height: 800px; opacity: 1; }
    }
</style>
